import { supabase } from '../config/supabase';

// Interface pour les données d'authentification
export interface AuthData {
  token: string;
  user: {
    uid: string;
    email: string;
    username?: string;
    isAdmin?: boolean;
  };
}

// Interface pour l'utilisateur complet
export interface UserData {
  id: string;
  username: string;
  email: string;
  isAdmin: boolean;
  createdAt: Date;
}

// Récupérer le profil (rôle admin, username) d'un utilisateur Supabase
const fetchProfile = async (userId: string): Promise<{ username: string; isAdmin: boolean } | null> => {
  const { data, error } = await supabase
    .from('profiles')
    .select('username, is_admin')
    .eq('id', userId)
    .single();

  if (error || !data) {
    console.error('Erreur lors de la récupération du profil:', error);
    return null;
  }

  return { username: data.username || '', isAdmin: data.is_admin || false };
};

// Service d'authentification Supabase
// Note: aucune inscription publique — les comptes admin sont créés manuellement
// via le dashboard Supabase (Authentication) + une ligne dans la table `profiles`.
const authService = {
  // Connexion d'un utilisateur existant
  async login(email: string, password: string): Promise<AuthData> {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });

    if (error || !data.session || !data.user) {
      console.error('Erreur lors de la connexion:', error);
      throw error || new Error('Connexion impossible');
    }

    const profile = await fetchProfile(data.user.id);

    return {
      token: data.session.access_token,
      user: {
        uid: data.user.id,
        email: data.user.email || email,
        username: profile?.username || email.split('@')[0],
        isAdmin: profile?.isAdmin || false,
      },
    };
  },

  // Déconnexion de l'utilisateur
  async logout(): Promise<void> {
    const { error } = await supabase.auth.signOut();
    if (error) {
      console.error('Erreur lors de la déconnexion:', error);
      throw error;
    }
  },

  // Récupérer l'utilisateur actuellement connecté
  async getCurrentUser(): Promise<UserData | null> {
    const { data } = await supabase.auth.getSession();
    const user = data.session?.user;
    if (!user) return null;

    const profile = await fetchProfile(user.id);
    if (!profile) return null;

    return {
      id: user.id,
      username: profile.username,
      email: user.email || '',
      isAdmin: profile.isAdmin,
      createdAt: new Date(user.created_at),
    };
  },

  // Récupérer le token de l'utilisateur connecté
  async getToken(): Promise<string | null> {
    const { data } = await supabase.auth.getSession();
    return data.session?.access_token || null;
  },

  // Vérifier si l'utilisateur est administrateur
  async isAdmin(): Promise<boolean> {
    const { data } = await supabase.auth.getSession();
    const user = data.session?.user;
    if (!user) return false;

    const profile = await fetchProfile(user.id);
    return profile?.isAdmin || false;
  },

  // Récupérer un utilisateur par ID (via la table profiles)
  async getUserById(uid: string): Promise<UserData | null> {
    const { data, error } = await supabase
      .from('profiles')
      .select('id, username, is_admin, created_at')
      .eq('id', uid)
      .single();

    if (error || !data) {
      console.error('Erreur lors de la récupération de l\'utilisateur par ID:', error);
      return null;
    }

    const { data: authData } = await supabase.auth.getSession();

    return {
      id: data.id,
      username: data.username || '',
      email: authData.session?.user.email || '',
      isAdmin: data.is_admin || false,
      createdAt: data.created_at ? new Date(data.created_at) : new Date(),
    };
  },
};

export default authService;
